import React, { useCallback, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { downloadMarkdownFile } from './downloadMarkdownFile.ts';

const i18n = defineMessages({
	exportMarkdownLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.export.markdown',
		description:
			'Menu item label shown inside the Export submenu of the page overflow menu. Clicking it downloads the current page as a Markdown (.md) file.',
		defaultMessage: 'Export to Markdown',
	},
});

export type ExportMarkdownMenuItemProps = {
	contentId: string;
	contentType?: string;
	pageTitle: string;
	contentStatus?: string;
	closeParentMenu?: () => void;
};

// The Confluence v2 REST API serves pages and blogposts from separate
// collections. A blogpost id does not exist under `/pages/{id}` (it 404s), so we
// must pick the collection that matches the content type. Classic pages and live
// docs are both `contentType === 'page'` and use the `pages` collection.
const getMarkdownExportEndpoint = (contentId: string, contentType?: string): string => {
	const collection = contentType === 'blogpost' ? 'blogposts' : 'pages';
	return `/wiki/api/v2/${collection}/${encodeURIComponent(contentId)}?body-format=markdown`;
};

type PageMarkdownResponse = {
	id?: string;
	title?: string;
	body?: {
		markdown?: {
			value?: string;
			representation?: string;
		};
	};
};

export const ExportMarkdownMenuItem: React.FC<ExportMarkdownMenuItemProps> = ({
	contentId,
	contentType,
	pageTitle,
	closeParentMenu,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isExporting, setIsExporting] = useState(false);

	const fireClickedAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'exportMenuItem',
				actionSubjectId: 'exportMarkdown',
				source: 'overflowMenu',
				attributes: {
					exportType: 'markdown',
					contentId,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, contentId]);

	const fireResultAnalytics = useCallback(
		(result: 'succeeded' | 'failed', extra?: Record<string, unknown>) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: result,
					actionSubject: 'exportMenuItem',
					actionSubjectId: 'exportMarkdown',
					source: 'overflowMenu',
					attributes: {
						exportType: 'markdown',
						contentId,
						...extra,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, contentId],
	);

	const handleClick = useCallback(async () => {
		if (isExporting) {
			return;
		}
		fireClickedAnalytics();
		setIsExporting(true);

		try {
			const response = await fetch(getMarkdownExportEndpoint(contentId, contentType), {
				method: 'GET',
				credentials: 'same-origin',
				headers: { Accept: 'application/json' },
			});

			if (!response.ok) {
				fireResultAnalytics('failed', {
					reason: response.status === 400 ? 'markdown-not-supported' : `http-${response.status}`,
				});
				return;
			}

			const data: PageMarkdownResponse = await response.json();
			const markdown = data?.body?.markdown?.value;
			const resolvedTitle = data?.title || pageTitle || 'page';

			if (typeof markdown !== 'string' || markdown.length === 0) {
				fireResultAnalytics('failed', { reason: 'missing-markdown' });
				return;
			}

			const escapedTitle = resolvedTitle.replace(/([\\`*_{}[\]()#+\-.!|>])/g, '\\$1');
			const prelude = `# ${escapedTitle}\n\n`;
			downloadMarkdownFile(`${prelude}${markdown}`, resolvedTitle);
			fireResultAnalytics('succeeded');
		} catch (error) {
			fireResultAnalytics('failed', {
				reason: 'network-error',
				message: error instanceof Error ? error.message : String(error),
			});
		} finally {
			setIsExporting(false);
			closeParentMenu?.();
		}
	}, [
		fireClickedAnalytics,
		fireResultAnalytics,
		isExporting,
		contentId,
		contentType,
		pageTitle,
		closeParentMenu,
	]);

	return (
		<DropdownItem
			onClick={handleClick}
			isDisabled={isExporting}
			testId="export-markdown-menu-item"
			aria-label={intl.formatMessage(i18n.exportMarkdownLabel)}
		>
			<FormattedMessage {...i18n.exportMarkdownLabel} />
		</DropdownItem>
	);
};
